/** The itinerary DELETE's 409 (`ItineraryDeleteConflict`), read for the refusal dialog.
 *  Nothing was deleted. `itinerary_in_use` names what depends on the itinerary in
 *  `dependents` (absent when the legacy protected-reference fallback refused; its
 *  `detail` says so); `itinerary_placeholder` is the operator's placeholder that new
 *  tours start from. Any other error is not a refusal and keeps the generic snackbar.
 *
 *  The grid's batch Delete applies the same refusal per item: the backend leaves the
 *  refused itineraries out of the task, and refuses with 409 `itinerary_delete_refused`
 *  (`ItineraryBatchDeleteRefused`) only when it refuses them all. */
import { ConsoleApiError } from '@/api/client';
import type { ItineraryBatchDeleteRefusal, ItineraryDeleteConflict, ItineraryDependents } from '@/api/itineraries';
import type { BatchRefusal, BatchRefusals } from '@/components/DataGrid/batchTypes';

import {
  DELETE_REFUSAL_REASON,
  DEPENDENT_COUNT_KEYS,
  batchDeleteLeftOut,
  batchDeleteRefusedTitle,
  dependentPhrases,
} from './words';

export interface ItineraryDeleteRefusal {
  code: ItineraryDeleteConflict['code'];
  /** The server's sentence: what the dialog says when no dependent is listed. */
  detail: string;
  /** The non-zero dependents in words, in the server's order; null when none were counted. */
  dependents: string[] | null;
}

/** A 409's body as a plain object, with the server's sentence (the error's message when
 *  the body has none), or null for any other error. */
function conflict(e: unknown): { body: Record<string, unknown>; detail: string } | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  // Typed as an object, but a proxy's HTML error page arrives as a string.
  const raw: unknown = e.body;
  const body = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  return { body, detail: typeof body.detail === 'string' && body.detail ? body.detail : e.message };
}

function isDependents(v: unknown): v is ItineraryDependents {
  if (!v || typeof v !== 'object') return false;
  const d = v as Record<string, unknown>;
  return DEPENDENT_COUNT_KEYS.every((k) => typeof d[k] === 'number');
}

/** The refusal behind a failed itinerary delete, or null when the error is anything else. */
export function itineraryDeleteRefusal(e: unknown): ItineraryDeleteRefusal | null {
  const refused = conflict(e);
  if (!refused) return null;
  const { body, detail } = refused;
  const { code } = body;
  if (code !== 'itinerary_in_use' && code !== 'itinerary_placeholder') return null;
  const listed = code === 'itinerary_in_use' && isDependents(body.dependents) ? dependentPhrases(body.dependents) : [];
  return { code, detail, dependents: listed.length > 0 ? listed : null };
}

function isBatchRefusal(v: unknown): v is ItineraryBatchDeleteRefusal {
  if (!v || typeof v !== 'object') return false;
  const { id, code } = v as Record<string, unknown>;
  return typeof id === 'number' && (code === 'itinerary_in_use' || code === 'itinerary_placeholder');
}

/** The refusal behind a batch delete the backend refused for every selected itinerary,
 *  for the grid's refusal dialog (each one links to its page), or null for any other
 *  error. */
export function itineraryBatchDeleteRefusal(e: unknown): BatchRefusal | null {
  const refused = conflict(e);
  if (refused?.body.code !== 'itinerary_delete_refused') return null;
  const { body, detail } = refused;
  const items = Array.isArray(body.refused) ? body.refused.filter(isBatchRefusal) : [];
  return {
    title: batchDeleteRefusedTitle(items.length),
    detail,
    items: items.map(({ id, code }) => ({ id, reason: DELETE_REFUSAL_REASON[code], to: `/itineraries/${id}` })),
  };
}

/** The grid's batch Delete says what the backend refused: how many it left out of the
 *  task, and every refused itinerary when it refused them all. */
export const itineraryBatchDeleteRefusals: BatchRefusals = {
  leftOut: batchDeleteLeftOut,
  refused: itineraryBatchDeleteRefusal,
};
